import {
  ACTION_TYPE,
  ACTION_TYPES,
  AGGREGATION_TYPE,
  AGGREGATION_TYPES,
} from '@monorepo/shared/schemas/common'
import { Save, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Textarea } from '@/shared/components/ui/textarea'
import type {
  ActionType,
  AgentActionFormData,
  AggregationType,
} from '../types/agent.types'
import { CostSignalSelector } from './CostSignalSelector'

interface AgentActionDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialAction?: AgentActionFormData
  defaultActionType?: ActionType
  onSave: (action: AgentActionFormData, costSignalIds: string[]) => void
  onCancel: () => void
}

export function AgentActionDrawer({
  open,
  onOpenChange,
  initialAction,
  defaultActionType = ACTION_TYPE.task,
  onSave,
  onCancel,
}: AgentActionDrawerProps) {
  const [action, setAction] = useState<AgentActionFormData>({
    id: initialAction?.id,
    action_name: initialAction?.action_name || '',
    action_type: initialAction?.action_type || defaultActionType,
    description: initialAction?.description || '',
    signal_name: initialAction?.signal_name || '',
    signal_code: initialAction?.signal_code || '',
    aggregation_type: initialAction?.aggregation_type || AGGREGATION_TYPE.count,
    unit_label: initialAction?.unit_label || '',
    value_field: initialAction?.value_field || '',
    cost_signal_ids: initialAction?.cost_signal_ids || [],
    fte_equivalent: initialAction?.fte_equivalent,
    revenue_per_unit: initialAction?.revenue_per_unit,
    default_price_cents: initialAction?.default_price_cents,
  })

  // Track selected cost signal IDs
  const [selectedCostSignalIds, setSelectedCostSignalIds] = useState<string[]>(
    initialAction?.cost_signal_ids || []
  )

  // Sync state when initialAction changes (for edit mode) or when drawer opens
  useEffect(() => {
    if (initialAction) {
      setAction({
        id: initialAction.id,
        action_name: initialAction.action_name || '',
        action_type: initialAction.action_type || defaultActionType,
        description: initialAction.description || '',
        signal_name: initialAction.signal_name || '',
        signal_code: initialAction.signal_code || '',
        aggregation_type:
          initialAction.aggregation_type || AGGREGATION_TYPE.count,
        unit_label: initialAction.unit_label || '',
        value_field: initialAction.value_field || '',
        cost_signal_ids: initialAction.cost_signal_ids || [],
        fte_equivalent: initialAction.fte_equivalent,
        revenue_per_unit: initialAction.revenue_per_unit,
        default_price_cents: initialAction.default_price_cents,
      })
      setSelectedCostSignalIds(initialAction.cost_signal_ids || [])
    } else {
      // Reset to empty form when adding new action
      setAction({
        id: undefined,
        action_name: '',
        action_type: defaultActionType,
        description: '',
        signal_name: '',
        signal_code: '',
        aggregation_type: AGGREGATION_TYPE.count,
        unit_label: '',
        value_field: '',
        cost_signal_ids: [],
        fte_equivalent: undefined,
        revenue_per_unit: undefined,
        default_price_cents: undefined,
      })
      setSelectedCostSignalIds([])
    }
  }, [initialAction, defaultActionType])

  const handleSave = () => {
    if (action.action_name && action.signal_name && action.signal_code) {
      onSave(
        { ...action, cost_signal_ids: selectedCostSignalIds },
        selectedCostSignalIds
      )
      onOpenChange(false)
    }
  }

  const handleCancel = () => {
    onCancel()
    onOpenChange(false)
  }

  // Check if value_field is required based on aggregation type
  const needsValueField = action.aggregation_type !== AGGREGATION_TYPE.count
  const hasRequiredValueField =
    !needsValueField || (action.value_field && action.value_field.length > 0)

  const isValid =
    action.action_name.length > 0 &&
    action.signal_name.length > 0 &&
    action.signal_code.length > 0 &&
    hasRequiredValueField

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {initialAction ? 'Edit Action' : 'Add New Action'}
          </DrawerTitle>
          <DrawerDescription>
            Configure action tracking and monetization for your agent
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-4 pb-6'>
          <div className='space-y-6'>
            {/* Section 1: Action Details */}
            <div className='space-y-4'>
              <div className='text-base font-medium text-muted-foreground'>
                Action Details
              </div>
              <div className='grid grid-cols-1 gap-4'>
                <div>
                  <Label htmlFor='actionName'>Action Name *</Label>
                  <Input
                    id='actionName'
                    value={action.action_name}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        action_name: e.target.value,
                      }))
                    }
                    placeholder='e.g., Send Email Campaign'
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    A descriptive name for this action
                  </p>
                </div>

                <div>
                  <Label htmlFor='actionType'>Action Type *</Label>
                  <Select
                    value={action.action_type}
                    onValueChange={(value: ActionType) =>
                      setAction(prev => ({ ...prev, action_type: value }))
                    }
                  >
                    <SelectTrigger className='mt-2'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_TYPES.map(type => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor='description'>Description</Label>
                  <Textarea
                    id='description'
                    value={action.description || ''}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder='Optional description of what this action does'
                    className='mt-2'
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className='border-t border-border'></div>

            {/* Section 2: Billable Signal */}
            <div className='space-y-4'>
              <div className='text-base font-medium text-muted-foreground'>
                Billable Signal
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <Label htmlFor='signalName'>Signal Name *</Label>
                  <Input
                    id='signalName'
                    value={action.signal_name}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        signal_name: e.target.value,
                      }))
                    }
                    placeholder='e.g., Email Sent'
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    Display name for the billable signal
                  </p>
                </div>

                <div>
                  <Label htmlFor='signalCode'>Signal Code *</Label>
                  <Input
                    id='signalCode'
                    value={action.signal_code}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        signal_code: e.target.value,
                      }))
                    }
                    placeholder='e.g., email_sent'
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    Unique identifier used in your code
                  </p>
                </div>

                <div>
                  <Label htmlFor='aggregation'>Aggregation Type *</Label>
                  <Select
                    value={action.aggregation_type}
                    onValueChange={(value: AggregationType) =>
                      setAction(prev => ({ ...prev, aggregation_type: value }))
                    }
                  >
                    <SelectTrigger className='mt-2'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AGGREGATION_TYPES.map(type => (
                        <SelectItem key={type} value={type}>
                          {type
                            .split('_')
                            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                            .join(' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor='unitLabel'>Unit Label</Label>
                  <Input
                    id='unitLabel'
                    value={action.unit_label || ''}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        unit_label: e.target.value,
                      }))
                    }
                    placeholder='e.g., emails, tokens, API calls'
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    Display name for the unit being measured
                  </p>
                </div>

                {/* Value Field - Required for non-count aggregations */}
                {needsValueField && (
                  <div className='md:col-span-2'>
                    <Label htmlFor='valueField'>Value Field *</Label>
                    <Input
                      id='valueField'
                      value={action.value_field || ''}
                      onChange={e =>
                        setAction(prev => ({
                          ...prev,
                          value_field: e.target.value,
                        }))
                      }
                      placeholder='e.g., amount, tokens, duration'
                      className='mt-2'
                    />
                    <p className='text-xs text-muted-foreground mt-1'>
                      The field name in your event data to{' '}
                      {action.aggregation_type}. Example: if your event is{' '}
                      <code>{`{"amount": 50}`}</code>, enter "amount"
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className='border-t border-border'></div>

            {/* Section 3: Cost Signals */}
            <CostSignalSelector
              selectedSignalIds={selectedCostSignalIds}
              onSelectionChange={setSelectedCostSignalIds}
            />

            {/* Divider */}
            <div className='border-t border-border'></div>

            {/* Section 4: Revenue Metrics */}
            <div className='space-y-4'>
              <div className='text-base font-medium text-muted-foreground'>
                Revenue & Value
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <Label htmlFor='fteEquivalent'>FTE Equivalent</Label>
                  <Input
                    id='fteEquivalent'
                    type='number'
                    step='0.1'
                    value={action.fte_equivalent || ''}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        fte_equivalent: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      }))
                    }
                    placeholder='0.0'
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    Full-time employee equivalent for this action
                  </p>
                </div>

                <div>
                  <Label htmlFor='revenuePerUnit'>Revenue per Unit ($)</Label>
                  <Input
                    id='revenuePerUnit'
                    type='number'
                    step='0.01'
                    value={action.revenue_per_unit || ''}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        revenue_per_unit: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      }))
                    }
                    placeholder='0.00'
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    Expected revenue generated per unit
                  </p>
                </div>

                <div>
                  <Label htmlFor='defaultPriceCents'>
                    Default Price (cents)
                  </Label>
                  <Input
                    id='defaultPriceCents'
                    type='number'
                    step='1'
                    value={action.default_price_cents || ''}
                    onChange={e =>
                      setAction(prev => ({
                        ...prev,
                        default_price_cents: e.target.value
                          ? parseInt(e.target.value)
                          : undefined,
                      }))
                    }
                    placeholder='0'
                    className='mt-2'
                  />
                  <p className='text-xs text-muted-foreground mt-1'>
                    Default pricing in cents
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DrawerFooter>
          <div className='flex gap-2 w-full'>
            <DrawerClose asChild>
              <Button
                variant='outline'
                onClick={handleCancel}
                className='flex-1'
              >
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button onClick={handleSave} disabled={!isValid} className='flex-1'>
              <Save className='w-4 h-4 mr-2' />
              {initialAction ? 'Update Action' : 'Save Action'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
